import classNames from "classnames";
import { kebabCase } from "lodash";
import { observer } from "mobx-react";
import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import Popup from "reactjs-popup";

import Info from "@/assets/svgs/info";
import { REPORT_ENABLED_MODULES } from "@/components/reports/constants";
import ModalWrapper from "@/components/shared/ModalWrapper";
import { useMainStore } from "@/contexts/Store";

import pointsIconActive from "../../../images/table-image/icon/points-icon-active.svg";
import pointsIcon from "../../../images/table-image/icon/points-icon.svg";
import { moduleInfo } from "./constants/module_info";

function HeadSelect() {
  // Import MobX stores
  const mainStore = useMainStore();

  const { themisModuleIdentifier: moduleIdentifier, workspaceID } =
    mainStore.context;
  const { isCurrentWorkspaceActive } = mainStore.workspaces;
  const { hasModuleWriteAccess } = mainStore.userPermissions;
  const selectedModuleInfo = moduleInfo.find(
    (module) => module.identifier === moduleIdentifier,
  );

  // State
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [isOpenModuleInfoModal, setIsOpenModuleInfoModal] = useState(false);

  const buttonClasses = classNames("export-points button-top", {
    "big-active": isOpenModal,
  });

  const infoButtonClasses = classNames("info-wrapper", {
    active: isOpenModuleInfoModal,
  });

  const showMoreButton =
    moduleIdentifier &&
    (mainStore.userPermissions.canSeeNotifications ||
      moduleIdentifier !== "risk_assessment");

  return (
    <>
      {selectedModuleInfo && (
        <Popup
          open={isOpenModuleInfoModal}
          onOpen={() => {
            setIsOpenModuleInfoModal(true);
          }}
          onClose={() => {
            setIsOpenModuleInfoModal(false);
          }}
          className="module-info"
          trigger={() => (
            <div className={infoButtonClasses}>
              <Info
                fill={isOpenModuleInfoModal ? "#fff" : "#353549"}
                style={{ width: 18, height: 18 }}
              />
            </div>
          )}
          on="click"
          position="left top"
        >
          <ModalWrapper>
            <h2>{selectedModuleInfo.name}</h2>
            <p className="module-info-content-description">
              {selectedModuleInfo.description}
            </p>
            <ol>
              {selectedModuleInfo.features.map((feature, index) => (
                <li key={index}>{feature}</li>
              ))}
            </ol>
            {selectedModuleInfo.link && (
              <div className="module-info-content-footer">
                <p>
                  Want to learn more?{" "}
                  <a href={selectedModuleInfo.link}>
                    Visit our module documentation
                  </a>
                </p>
              </div>
            )}
          </ModalWrapper>
        </Popup>
      )}
      {showMoreButton && (
        <Popup
          position="bottom right"
          trigger={() => (
            <div data-testid="header-popup-more">
              <button className={buttonClasses}>
                <img
                  className="points-icon"
                  src={pointsIcon}
                  alt="points-icon"
                />
                <img
                  className="points-icon-active"
                  src={pointsIconActive}
                  alt="points-icon-active"
                />
                More
              </button>
            </div>
          )}
          open={isOpenModal}
          onOpen={() => {
            setIsOpenModal(true);
          }}
          onClose={() => {
            setIsOpenModal(false);
          }}
          keepTooltipInside
        >
          <div
            className="select-link"
            data-testid="page-context-menu"
            onClick={() => setIsOpenModal(false)}
          >
            <ul>
              {[
                "control_mapping",
                "documents",
                "policy",
                "procedures",
                "training",
              ].includes(moduleIdentifier) && (
                <li>
                  <NavLink
                    to={`/workspaces/${workspaceID}/modules/${kebabCase(
                      moduleIdentifier,
                    )}/archived`}
                  >
                    View Archived
                  </NavLink>
                </li>
              )}

              {moduleIdentifier !== "risk_assessment" && (
                <li>
                  <NavLink
                    to={`/workspaces/${workspaceID}/modules/${kebabCase(
                      moduleIdentifier,
                    )}/audit_trail`}
                  >
                    View Activity
                  </NavLink>
                </li>
              )}

              {mainStore.userPermissions.canSeeNotifications && (
                <li>
                  <NavLink
                    to={`/workspaces/${workspaceID}/modules/${kebabCase(
                      moduleIdentifier,
                    )}/notifications`}
                  >
                    Module Notifications
                  </NavLink>
                </li>
              )}

              {["policy", "marketing"].includes(moduleIdentifier) &&
                isCurrentWorkspaceActive &&
                hasModuleWriteAccess && (
                  <li>
                    <NavLink
                      to={`/workspaces/${workspaceID}/modules/${moduleIdentifier}/manage-approvals-steps`}
                    >
                      Manage Approvals
                    </NavLink>
                  </li>
                )}

              {REPORT_ENABLED_MODULES.includes(moduleIdentifier) && (
                <li>
                  <NavLink to={`/workspace-reports/${moduleIdentifier}`}>
                    Reports
                  </NavLink>
                </li>
              )}
            </ul>
          </div>
        </Popup>
      )}
    </>
  );
}

export default observer(HeadSelect);
